// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react';

interface LayeredComparisonRendererProps {
  sectionName: string;
  sectionData: any;
}

/**
 * Specialized renderer for layered comparison output.
 * Transforms layered data into professional visualizations matching the PDF example.
 */
export const LayeredComparisonRenderer: React.FC<LayeredComparisonRendererProps> = ({ 
  sectionName, 
  sectionData 
}) => {
  // Executive Summary - Visual metrics display
  if (sectionName === 'executive_summary') {
    return <ExecutiveSummary data={sectionData} />;
  }

  // Layer 1: Document Structure - Side-by-side exam cards
  if (sectionName === 'layer_1_document_structure') {
    return <Layer1DocumentStructure data={sectionData} />;
  }

  // Layer 3: Problem-by-Problem Concept Analysis
  if (sectionName === 'layer_3_problem_by_problem_concept_analysis') {
    return <Layer3ProblemByProblemConceptAnalysis data={sectionData} />;
  }

  // Layer 2: Topic Comparison - Comparison table
  if (sectionName === 'layer_2_concept_coverage_analysis') {
    return <Layer2ConceptCoverageAnalysis data={sectionData} />;
  }


  // Default: Return null, let parent renderer handle it
  return null;
};

// Layer 1: Document Structure - Compact exam cards
const Layer1DocumentStructure: React.FC<{ data: any }> = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-4">
      {data.map((exam: any, idx: number) => (
        <div 
          key={idx} 
          className="bg-white border border-gray-300 rounded p-4"
        >
          <h4 className="text-base font-bold text-gray-900 mb-3 border-b border-gray-300 pb-2">
            {exam.exam_id || `Exam ${idx + 1}`}
          </h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Problems</span>
              <span className="text-xl font-bold text-gray-900">{exam.problems_count || 0}</span>
            </div>
            
            {exam.total_subparts && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Subparts</span>
                <span className="font-semibold text-gray-900">{exam.total_subparts}</span>
              </div>
            )}
            
            {exam.primary_focus && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <div className="text-xs text-gray-600 mb-1">Primary Focus</div>
                <div className="text-sm font-medium text-gray-900">{exam.primary_focus}</div>
              </div>
            )}
            
            {exam.assessment_style && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <div className="text-xs text-gray-600 mb-1">Assessment Style</div>
                <div className="text-sm font-medium text-gray-900">{exam.assessment_style}</div>
              </div>
            )}
            
            {exam.problem_complexity && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <div className="text-xs text-gray-600 mb-1">Problem Complexity</div>
                <div className="text-sm font-medium text-gray-900">{exam.problem_complexity}</div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Layer 3: Problem-by-Problem Concept Analysis - Detailed problem breakdown
const Layer3ProblemByProblemConceptAnalysis: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== 'object') return null;

  const examEntries = Object.entries(data);
  
  // Helper function to get badge style based on difficulty (yellow gradient, WCAG AA accessible)
  const getDifficultyStyle = (difficulty: string): React.CSSProperties => {
    switch (difficulty.toLowerCase()) {
      case 'basic':
        return { backgroundColor: '#fffbcc', color: '#5c4300' }; // pale cream yellow, ~8.6:1 contrast
      case 'intermediate':
        return { backgroundColor: '#ffd200', color: '#000000' }; // Maryland Gold, ~12:1 contrast
      case 'advanced':
        return { backgroundColor: '#c9a800', color: '#000000' }; // deep golden yellow, ~9:1 contrast
      default:
        return { backgroundColor: '#6b7280', color: '#ffffff' };
    }
  };

  // Helper function to format the problem title from topics
  const getProblemTitle = (problem: any): string => {
    if (problem.topics && Array.isArray(problem.topics) && problem.topics.length > 0) {
      return problem.topics.join(' and ');
    }
    return 'Problem';
  };


  // Helper function to render time estimate badge
  const renderTimeEstimate = (estimatedTimeMinutes: number | undefined): JSX.Element | null => {
    if (!estimatedTimeMinutes) return null;
    return (
      <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium whitespace-nowrap">
        {estimatedTimeMinutes} min
      </span>
    );
  };

  return (
    <div className="grid grid-cols-2 gap-6">
      {examEntries.map(([examId, problems]: [string, any]) => (
        <div key={examId}>
          {/* Exam Header */}
          <h3 className="text-lg font-bold ">
            {examId}
          </h3>
          
          {/* Note if projected */}
          {examId.toLowerCase().includes('spring 2025') && (
            <p className="text-sm italic text-gray-600 mb-4">
              Based on syllabus progression and standard precalculus assessment patterns
            </p>
          )}
          
          {/* Problems List */}
          <div className="space-y-4">
            {Array.isArray(problems) && problems.map((problem: any, idx: number) => (
              <div 
                key={idx}
                className=" bg-gray-50"
              >
                {/* Problem Header with Number, Title, and Badges */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <span className="font-bold text-gray-900">
                      Problem {problem.problem_number}:{' '}
                    </span>
                    <span className="text-gray-900">
                      {getProblemTitle(problem)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                      style={getDifficultyStyle(problem.difficulty_rating)}
                    >
                      {problem.difficulty_rating}
                    </span>
                    {renderTimeEstimate(problem.estimated_time_minutes)}
                  </div>
                </div>
                
                {/* Concepts Tested */}
                {problem.concepts_tested && (
                  <div className="mb-2">
                    <span className="font-semibold text-gray-900 text-sm">Concepts Tested: </span>
                    <span className="text-gray-800 text-sm">
                      {problem.concepts_tested}
                    </span>
                  </div>
                )}
                
                {/* Cognitive Level */}
                {problem.cognitive_level && (
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">Cognitive Level: </span>
                    <span className="text-gray-800 text-sm">
                      {problem.cognitive_level}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


// Layer 2: Concept Coverage Analysis
const Layer2ConceptCoverageAnalysis: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== 'object') return null;

  // Function to determine cell color based on intensity category using UMD brand colors
  const getCellColorByIntensity = (intensity: string | null | undefined): string => {
    if (!intensity || typeof intensity !== 'string') return '#e6e6e6';
    
    switch (intensity.toLowerCase()) {
      case 'high':
        return '#e21833'; // Maryland Red
      case 'med-high':
        return '#ffd200'; // Maryland Gold
      case 'medium':
        return '#ad7231'; // Bronze Testudo
      case 'low-med':
        return '#7f7f7f'; // Medium Gray
      case 'low':
        return '#e6e6e6'; // Light Gray
      default:
        return '#e6e6e6';
    }
  };

  // Function to determine text color for contrast (WCAG compliant)
  const getTextColorByIntensity = (intensity: string | null | undefined): string => {
    if (!intensity || typeof intensity !== 'string') return '#000000';
    
    switch (intensity.toLowerCase()) {
      case 'high':
      case 'medium':
        return '#FFFFFF'; // White text on red and bronze
      case 'med-high':
      case 'low-med':
      case 'low':
      default:
        return '#000000'; // Black text on gold and grays
    }
  };

  // Fallback: Function to determine cell color based on percentage (for backwards compatibility)
  const getCellColor = (percentage: number): string => {
    if (percentage >= 33) return '#e21833'; // High
    if (percentage >= 20) return '#ffd200'; // Med-High
    if (percentage >= 10) return '#ad7231'; // Medium
    if (percentage >= 5) return '#7f7f7f'; // Low-Med
    return '#e6e6e6'; // Low
  };

  // Fallback: Function to determine text color based on percentage
  const getTextColor = (percentage: number): string => {
    if (percentage >= 33) return '#FFFFFF'; // High
    if (percentage >= 10) return '#FFFFFF'; // Medium
    return '#000000'; // Others
  };

  return (
    <div className="space-y-6">
      {/* Heatmap Visualization */}
      {data.topic_coverage_heatmap && (
        <div className="bg-white border border-gray-300 rounded-lg p-3">
          <h4 className="text-sm font-bold text-gray-900 mb-3">Topic Coverage Intensity</h4>
          
          {/* Heatmap Table */}
          <div className="overflow-x-auto">
            <table className="border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-gray-900" style={{ width: '200px' }} aria-label="Topic">
                  </th>
                  {data.topic_coverage_heatmap.exams?.map((exam: string, idx: number) => (
                    <th 
                      key={idx} 
                      className="text-center px-2 py-2 text-sm font-bold"
                      style={{ 
                        color: '#e21833',
                        width: '280px'
                      }}
                    >
                      {exam}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.topic_coverage_heatmap.topics?.map((topic: string, topicIdx: number) => (
                <tr key={topicIdx}>
                    <td className="py-2 px-3 text-xs text-gray-900 font-medium">
                      {topic}
                    </td>
                    {data.topic_coverage_heatmap.coverage_matrix[topicIdx]?.map((cellData: any, examIdx: number) => {
                      // Handle two formats for backward compatibility:
                      // 1. String: "High", "Med-High", etc. (NEW)
                      // 2. Number: 50 (LEGACY)
                      
                      let intensity: string | null = null;
                      let percentage: number | null = null;
                      let backgroundColor: string;
                      let textColor: string;
                      
                      if (typeof cellData === 'string') {
                        // Format 1: String intensity (new format)
                        intensity = cellData;
                        backgroundColor = getCellColorByIntensity(intensity);
                        textColor = getTextColorByIntensity(intensity);
                      } else if (typeof cellData === 'number') {
                        // Format 2: Legacy number (percentage only)
                        percentage = cellData;
                        backgroundColor = getCellColor(percentage);
                        textColor = getTextColor(percentage);
                      } else {
                        // Default fallback for unexpected data
                        backgroundColor = '#e6e6e6';
                        textColor = '#000000';
                      }

                      return (
                        <td
                          key={examIdx}
                          className="text-center py-2 px-2"
                        >
                          <div
                          className="w-full py-2 rounded-full text-sm font-bold border-2 border-white"
                            style={{
                              backgroundColor,
                              color: textColor
                            }}
                          >
                            {intensity ? (
                              // New format: display intensity
                              <div>{intensity}</div>
                            ) : percentage !== null ? (
                              // Legacy format: display percentage
                              <div>{percentage}%</div>
                            ) : (
                              // No valid data
                              <div>-</div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Color Legend */}
          <div className="mt-3 flex items-center justify-start gap-3 text-xs">
            <span className="text-gray-700 font-semibold">Coverage Intensity:</span>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: '#e21833' }}></div>
              <span className="text-gray-600">High (≥33%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: '#ffd200' }}></div>
              <span className="text-gray-600">Med-High (20-32%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: '#ad7231' }}></div>
              <span className="text-gray-600">Medium (10-19%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: '#7f7f7f' }}></div>
              <span className="text-gray-600">Low-Med (5-9%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 rounded border border-gray-400" style={{ backgroundColor: '#e6e6e6' }}></div>
              <span className="text-gray-600">Low (&lt;5%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Existing three-column layout for shared/unique concepts */}
      <div className="grid grid-cols-3 gap-4">
      {/* Shared Core Concepts */}
      {data.shared_core_concepts && Array.isArray(data.shared_core_concepts) && data.shared_core_concepts.length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <h4 className="text-sm font-bold text-gray-900 mb-3">
            Shared Core Concepts
          </h4>
          <ul className="space-y-2">
            {data.shared_core_concepts.map((concept: string, idx: number) => (
              <li key={idx} className="text-sm text-gray-800 flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                <span>{concept}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unique Emphasis - Red/Pink */}
      {data.unique_emphasis_per_exam && Object.keys(data.unique_emphasis_per_exam).length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <h4 className="text-sm font-bold text-gray-900 mb-3">
            Unique Emphasis
          </h4>
          <div className="space-y-4">
            {Object.entries(data.unique_emphasis_per_exam).map(([examId, data]: [string, any]) => (
              <div key={examId}>
                <h5 className="text-xs font-semibold text-gray-900 mb-2">
                  {examId}
                </h5>
                {/* Handle both array and object with emphasis field */}
                {typeof data === 'object' && data.emphasis ? (
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {data.emphasis}
                  </p>
                ) : Array.isArray(data) ? (
                  <ul className="space-y-2">
                    {data.map((item: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-800 flex items-start gap-2">
                        <span className="text-gray-500 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unique Coverage */}
      {data.unique_coverage_per_exam && Object.keys(data.unique_coverage_per_exam).length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <h4 className="text-sm font-bold text-gray-900 mb-3">
            Unique Coverage
          </h4>
          <div className="space-y-4">
            {Object.entries(data.unique_coverage_per_exam).map(([examId, topics]: [string, any]) => (
              <div key={examId}>
                <h5 className="text-xs font-semibold text-gray-900 mb-2">
                  {examId}
                </h5>
                {Array.isArray(topics) && (
                  <ul className="space-y-2">
                    {topics.map((topic: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-800 flex items-start gap-2">
                        <span className="text-gray-500 mt-0.5">•</span>
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};



// Executive Summary - Compact and clean display
const ExecutiveSummary: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== 'object') return null;

  const { overall_alignment, key_metrics, primary_finding, overall_summary, critical_differences } = data;

  return (
    <div className="space-y-4">
      {/* Overall Summary - Top description */}
      {overall_summary && (
        <div className="text-gray-800 text-base leading-relaxed pb-4 border-b border-gray-300">
          {overall_summary}
        </div>
      )}

      {/* Key Metrics - Horizontal Compact Display */}
      {key_metrics && Array.isArray(key_metrics) && key_metrics.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {key_metrics.map((metric: string, idx: number) => {
            // Expected format: "X category (Exam Name) vs Y category (Exam Name)"
            // Example: "4 problems (Gruber Exam) vs 5 problems (Spring 2024)"
            
            // Simple split on " vs " to get left and right sides
            const parts = metric.split(' vs ');
            
            if (parts.length === 2) {
              const leftSide = parts[0].trim();
              const rightSide = parts[1].trim();
              
              // Extract category from the left side (everything after the number/percentage)
              // Match: number/percentage, then capture the rest
              // const categoryMatch = leftSide.match(/^[\d.]+%?\s+(.+)$/);
              // const category = categoryMatch ? categoryMatch[1].replace(/\([^)]+\)/, '').trim() : '';
              
              return (
                <div 
                  key={idx} 
                  className="bg-gray-50 rounded border border-gray-200 p-3 text-center flex-1 min-w-[180px]"
                >
                  <div className="text-lg text-black mb-1">
                    {leftSide} <span className="text-sm">vs</span> {rightSide}
                  </div>
                  {/*{category && (*/}
                  {/*  <div className="text-xs text-black capitalize">*/}
                  {/*    {category}*/}
                  {/*  </div>*/}
                  {/*)}*/}
                </div>
              );
            }
            
            // Fallback: just display the metric as-is
            return (
              <div 
                key={idx} 
                className="bg-gray-50 rounded border border-gray-200 p-3 text-center flex-1 min-w-[180px]"
              >
                <div className="text-base text-black">
                  {metric}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Overall Alignment Score */}
      {overall_alignment !== undefined && overall_alignment !== null && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-700">Overall Similarity:</div>
            <div className="text-2xl font-bold text-gray-900">
              {(overall_alignment * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* Primary Finding */}
      {primary_finding && (
        <div className="bg-gray-50 border border-gray-300 rounded p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Key Finding</div>
          <p className="text-sm text-gray-800 leading-relaxed">
            {primary_finding}
          </p>
        </div>
      )}

      {/* Critical Differences */}
      {critical_differences && Array.isArray(critical_differences) && critical_differences.length > 0 && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Critical Differences</div>
          <ul className="space-y-2">
            {critical_differences.map((diff: string, idx: number) => (
              <li 
                key={idx} 
                className="flex items-start gap-2 text-sm text-gray-800"
              >
                <span className="text-gray-500">•</span>
                <span>{diff}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LayeredComparisonRenderer;